"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
  useActionState,
} from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import type { DateRange } from "react-day-picker";
import {
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Minus,
  Plus,
  Search,
  X,
} from "lucide-react";
import { submitInquiry, type InquiryState } from "@/lib/actions/inquiry";
import { Button } from "@/components/ui/Button";
import { Input, Label, Textarea } from "@/components/ui/Fields";
import { SpriteIcon } from "@/components/ui/SpriteIcon";
import { cn } from "@/lib/format";
import { media } from "@/lib/media";
import type { DestinationList } from "@/lib/dto";

const DayPicker = dynamic(
  () => import("@/components/home/LazyDayPicker").then((m) => m.DayPicker),
  {
    ssr: false,
    loading: () => (
      <div className="grid min-h-[18rem] place-items-center text-sm text-muted-soft">
        Loading calendar…
      </div>
    ),
  },
);

const DURATION_BANDS = [
  {
    id: "day",
    label: "Day Trips",
    sub: "1 Day",
    min: 1,
    max: 1,
    blurb: "Single-day tours with no overnight stay.",
  },
  {
    id: "short",
    label: "Short Trips",
    sub: "2–5 Days",
    min: 2,
    max: 5,
    blurb: "Perfect for quick getaways and short treks.",
  },
  {
    id: "classic",
    label: "Classic Trips",
    sub: "6–12 Days",
    min: 6,
    max: 12,
    blurb: "Best for moderate treks and cultural exploration.",
  },
  {
    id: "extended",
    label: "Extended Trips",
    sub: "13+ Days",
    min: 13,
    max: 60,
    blurb: "Long treks and immersive travel experiences.",
  },
] as const;

type DurationBandId = (typeof DURATION_BANDS)[number]["id"];

const GROUP_TYPES = ["couple", "family", "friends", "solo"] as const;
type GroupType = (typeof GROUP_TYPES)[number];

const BUDGET_MIN = 100;
const BUDGET_MAX = 12000;
const BUDGET_STEP = 100;

const STEPS = [
  "Destination",
  "Duration",
  "Group",
  "Budget",
  "Dates",
  "Trips",
  "Contact",
] as const;

export type PlannerPackage = {
  id: number;
  href: string;
  title: string;
  image: string | null;
  imageAlt: string;
  price: string | null;
  duration: string | null;
  durationValue: number;
  priceValue: number;
  destinationSlug: string | null;
};

type Props = {
  destinations: DestinationList[];
  packages: PlannerPackage[];
};

type GroupSize = {
  groupType: GroupType;
  adult: number;
  children: number;
} | null;

function formatUsd(value: number): string {
  return `US$${value.toLocaleString("en-US")}`;
}

function formatGroupLabel(groupSize: GroupSize): string {
  if (!groupSize) return "";
  if (groupSize.groupType === "couple") return "Couple";
  if (groupSize.groupType === "solo") return "Solo";
  const typeLabel = groupSize.groupType === "family" ? "Family" : "Friends";
  const parts: string[] = [];
  if (groupSize.adult > 0) parts.push(`${groupSize.adult} Adult${groupSize.adult !== 1 ? "s" : ""}`);
  if (groupSize.children > 0) parts.push(`${groupSize.children} Child${groupSize.children !== 1 ? "ren" : ""}`);
  return parts.length ? `${typeLabel} (${parts.join(", ")})` : typeLabel;
}

function formatDateRange(range: DateRange | undefined): string {
  if (!range?.from) return "";
  const opts: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };
  const from = range.from.toLocaleDateString("en-GB", opts);
  if (!range.to) return from;
  const to = range.to.toLocaleDateString("en-GB", opts);
  return `${from} – ${to}`;
}

export function TripPlannerWizard({ destinations, packages }: Props) {
  const [step, setStep] = useState(0);
  const [destinationSlug, setDestinationSlug] = useState<string | null>(null);
  const [destinationQuery, setDestinationQuery] = useState("");
  const [durationBand, setDurationBand] = useState<DurationBandId | null>(null);
  const [groupSize, setGroupSize] = useState<GroupSize>(null);
  const [budget, setBudget] = useState<[number, number]>([BUDGET_MIN, BUDGET_MAX]);
  const [flexibleBudget, setFlexibleBudget] = useState<"yes" | "no" | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [decideLater, setDecideLater] = useState(false);

  const selectedDuration = DURATION_BANDS.find((b) => b.id === durationBand) ?? null;

  const matches = useMemo(() => {
    return packages.filter((pkg) => {
      if (destinationSlug && pkg.destinationSlug !== destinationSlug) return false;
      if (selectedDuration && (pkg.durationValue < selectedDuration.min || pkg.durationValue > selectedDuration.max))
        return false;
      if (pkg.priceValue > 0 && (pkg.priceValue < budget[0] || pkg.priceValue > budget[1])) return false;
      return true;
    });
  }, [packages, destinationSlug, selectedDuration, budget]);

  const selectedDestinationName = destinations.find((d) => d.slug === destinationSlug)?.name ?? null;

  const filteredDestinations = useMemo(() => {
    const q = destinationQuery.trim().toLowerCase();
    if (!q) return destinations;
    return destinations.filter((d) => d.name.toLowerCase().includes(q));
  }, [destinations, destinationQuery]);

  const canAdvance =
    (step === 0 && destinationSlug !== null) ||
    (step === 1 && durationBand !== null) ||
    (step === 2 && groupSize !== null) ||
    (step === 3 && flexibleBudget !== null) ||
    (step === 4 && (decideLater || Boolean(dateRange?.from && dateRange?.to))) ||
    step === 5;

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const resetFrom = (from: "destination" | "duration" | "group" | "budget" | "dates") => {
    if (from === "destination") {
      setDestinationSlug(null);
      setDestinationQuery("");
    }
    if (from === "destination" || from === "duration") setDurationBand(null);
    if (from === "destination" || from === "duration" || from === "group") setGroupSize(null);
    if (from !== "dates") {
      setBudget([BUDGET_MIN, BUDGET_MAX]);
      setFlexibleBudget(null);
    }
    setDateRange(undefined);
    setDecideLater(false);
  };

  const progressPercent = (Math.min(step, 4) / 4) * 100;

  return (
    <div className="planner-header-glow relative mx-auto max-w-5xl rounded-[2rem] px-3 pb-3 pt-6 sm:px-6 sm:pt-10">
      {/* Selected filter pills */}
      {destinationSlug || durationBand || groupSize || flexibleBudget ? (
        <ul className="mb-6 flex flex-wrap items-center justify-center gap-2.5">
          {destinationSlug ? (
            <FilterPill
              label="Destination"
              value={selectedDestinationName ?? ""}
              onRemove={() => {
                resetFrom("destination");
                setStep(0);
              }}
            />
          ) : null}
          {selectedDuration ? (
            <FilterPill
              label="Duration"
              value={`${selectedDuration.label} (${selectedDuration.sub})`}
              onRemove={() => {
                resetFrom("duration");
                setStep(1);
              }}
            />
          ) : null}
          {groupSize ? (
            <FilterPill
              label="Group"
              value={formatGroupLabel(groupSize)}
              onRemove={() => {
                resetFrom("group");
                setStep(2);
              }}
            />
          ) : null}
          {flexibleBudget ? (
            <FilterPill
              label="Budget"
              value={`${formatUsd(budget[0])} – ${formatUsd(budget[1])} (${flexibleBudget === "yes" ? "Flexible" : "Fixed"})`}
              onRemove={() => {
                setBudget([BUDGET_MIN, BUDGET_MAX]);
                setFlexibleBudget(null);
                setStep(3);
              }}
            />
          ) : null}
        </ul>
      ) : null}

      {/* Progress bar (hidden on results/contact) */}
      {step < 5 ? (
        <div className="mb-8 px-3 sm:px-2" aria-label={`Trip planner progress: step ${step + 1} of 5`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-primary">Build your trip</p>
            <p className="shrink-0 text-sm font-medium text-muted-soft" aria-live="polite">
              <span className="font-extrabold text-headings">Step {step + 1}</span> of 5
            </p>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-line">
            <div
              className="h-1.5 rounded-full bg-gradient-to-r from-primary to-secondary transition-[width] duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <ol className="mt-3 hidden grid-cols-5 gap-2 sm:grid">
            {STEPS.slice(0, 5).map((label, index) => (
              <li
                key={label}
                className={cn(
                  "truncate text-xs font-semibold",
                  index === step ? "text-headings" : index < step ? "text-primary" : "text-muted-soft",
                )}
                aria-current={index === step ? "step" : undefined}
              >
                {label}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <div className="relative overflow-hidden rounded-2xl border border-line bg-white p-5 shadow-xl shadow-headings/[0.06] sm:p-8 lg:p-10">
        <div key={step} className="planner-step">
        {step === 0 ? (
          <DestinationStep
            destinations={filteredDestinations}
            query={destinationQuery}
            onQueryChange={setDestinationQuery}
            selectedSlug={destinationSlug}
            onSelect={(slug) => {
              setDestinationSlug(slug);
              goNext();
            }}
          />
        ) : null}

        {step === 1 ? (
          <DurationStep
            selected={durationBand}
            onSelect={(id) => {
              setDurationBand(id);
              goNext();
            }}
          />
        ) : null}

        {step === 2 ? (
          <GroupSizeStep
            groupSize={groupSize}
            onSelectType={(type) => {
              if (type === "family" || type === "friends") {
                setGroupSize({ groupType: type, adult: groupSize?.adult || 2, children: groupSize?.children || 0 });
              } else {
                setGroupSize({ groupType: type, adult: 0, children: 0 });
              }
            }}
            onAdultChange={(adult) => setGroupSize((g) => (g ? { ...g, adult } : g))}
            onChildrenChange={(children) => setGroupSize((g) => (g ? { ...g, children } : g))}
            onContinue={goNext}
          />
        ) : null}

        {step === 3 ? (
          <BudgetStep
            budget={budget}
            onBudgetChange={setBudget}
            flexible={flexibleBudget}
            onFlexibleChange={(v) => {
              setFlexibleBudget(v);
              goNext();
            }}
          />
        ) : null}

        {step === 4 ? (
          <DepartureStep
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            decideLater={decideLater}
            onDecideLaterChange={(checked) => {
              setDecideLater(checked);
              if (checked) setDateRange(undefined);
            }}
            onContinue={goNext}
          />
        ) : null}

        {step === 5 ? (
          <ResultsStep matches={matches} destinationName={selectedDestinationName} onPickTrip={goNext} />
        ) : null}

        {step === 6 ? (
          <ContactStep
            destinationName={selectedDestinationName}
            durationLabel={selectedDuration ? `${selectedDuration.label} (${selectedDuration.sub})` : undefined}
            groupLabel={formatGroupLabel(groupSize) || undefined}
            budgetLabel={flexibleBudget ? `${formatUsd(budget[0])} – ${formatUsd(budget[1])}` : undefined}
            dateLabel={decideLater ? "Decide later" : formatDateRange(dateRange) || undefined}
            matchCount={matches.length}
          />
        ) : null}
        </div>

        {step >= 1 && step <= 5 ? (
          <div className="mt-8 flex items-center justify-between border-t border-line pt-6">
            <Button variant="ghost" size="sm" onClick={goBack}>
              <ChevronLeft size={16} aria-hidden /> Back
            </Button>
            {step === 5 ? (
              <Button variant="primary" size="sm" onClick={goNext}>
                Enquire about a trip <ChevronRight size={16} aria-hidden />
              </Button>
            ) : step !== 2 && step !== 3 && step !== 4 ? (
              <Button variant="primary" size="sm" onClick={goNext} disabled={!canAdvance}>
                Continue <ChevronRight size={16} aria-hidden />
              </Button>
            ) : (
              <span />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FilterPill({ label, value, onRemove }: { label: string; value: string; onRemove: () => void }) {
  return (
    <li className="flex items-center gap-1.5 text-sm">
      <span className="font-semibold text-headings">{label}:</span>
      <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white py-1.5 pl-3 pr-1.5 text-sm font-medium leading-none text-headings shadow-sm transition-colors hover:border-primary/50">
        {value}
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${label} filter`}
          className="grid h-6 w-6 place-items-center rounded-full text-muted-soft transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary cursor-pointer"
        >
          <X size={14} aria-hidden />
        </button>
      </span>
    </li>
  );
}

function StepHeading({ children, lede }: { children: React.ReactNode; lede?: string }) {
  return (
    <div className="mb-7 text-center">
      <h2 className="font-display text-xl font-extrabold leading-tight text-headings sm:text-2xl lg:text-3xl">{children}</h2>
      {lede ? <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-muted-soft">{lede}</p> : null}
    </div>
  );
}

function CheckBadge() {
  return (
    <i
      aria-hidden
      className="absolute right-4 top-4 grid h-6 w-6 place-items-center rounded-full border border-success bg-success text-white shadow-lg shadow-success/40"
    >
      <CheckCheck size={13} strokeWidth={2.5} />
    </i>
  );
}

// ─── Step 1: Destination ────────────────────────────────────────────────────

function DestinationStep({
  destinations,
  query,
  onQueryChange,
  selectedSlug,
  onSelect,
}: {
  destinations: DestinationList[];
  query: string;
  onQueryChange: (v: string) => void;
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
}) {
  return (
    <div>
      <StepHeading>
        What&rsquo;s <span className="text-primary">your pick</span> for your next vacation?
      </StepHeading>

      <div className="mx-auto max-w-2xl">
        <div className="relative flex items-center rounded-full border border-line bg-white p-1.5 shadow-sm transition-shadow focus-within:border-primary/50 focus-within:shadow-lg focus-within:shadow-primary/10">
          <Search size={18} className="ml-3 shrink-0 text-muted-soft" aria-hidden />
          <input
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search a destination…"
            aria-label="Search destinations"
            className="min-h-[42px] w-full border-0 bg-transparent px-3 text-sm font-medium text-headings placeholder:text-muted-soft focus:outline-none"
          />
        </div>

        <ul className="mt-5 grid max-h-96 grid-cols-2 gap-3 overflow-y-auto p-1 sm:grid-cols-3">
          {destinations.map((dest) => {
            const isSelected = selectedSlug === dest.slug;
            const image = media(dest.featured_image);
            return (
              <li key={dest.slug}>
                <button
                  type="button"
                  onClick={() => onSelect(dest.slug)}
                  aria-pressed={isSelected}
                  className={cn(
                    "group relative flex w-full flex-col overflow-hidden rounded-xl border text-left transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary cursor-pointer",
                    isSelected
                      ? "-translate-y-1 border-primary shadow-lg shadow-primary/20"
                      : "border-line bg-white shadow-sm hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10",
                  )}
                >
                  <figure className="image-slot relative aspect-[4/3] w-full overflow-hidden before:pt-[75%]">
                    {image ? (
                      <Image
                        src={image.src}
                        alt={image.alt || dest.name}
                        fill
                        sizes="(min-width: 640px) 33vw, 50vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : null}
                    <div
                      aria-hidden
                      className="absolute inset-0 z-20 bg-gradient-to-t from-black/55 via-black/0 to-transparent"
                    />
                    <span className="absolute inset-x-0 bottom-0 z-20 px-2.5 py-2">
                      <span className="block text-sm font-bold leading-tight text-white drop-shadow-sm">{dest.name}</span>
                      {dest.package_count ? (
                        <span className="text-xs font-medium text-white/80">{dest.package_count} trips</span>
                      ) : null}
                    </span>
                    {isSelected ? (
                      <i
                        aria-hidden
                        className="absolute right-2 top-2 z-20 grid h-6 w-6 place-items-center rounded-full border border-success bg-success text-white shadow-lg shadow-success/40"
                      >
                        <CheckCheck size={13} strokeWidth={2.5} />
                      </i>
                    ) : null}
                  </figure>
                </button>
              </li>
            );
          })}
          {destinations.length === 0 ? (
            <li className="col-span-full py-6 text-center text-sm text-muted-soft">No destinations match your search.</li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}

// ─── Step 2: Duration ───────────────────────────────────────────────────────

function DurationStep({
  selected,
  onSelect,
}: {
  selected: DurationBandId | null;
  onSelect: (id: DurationBandId) => void;
}) {
  return (
    <div>
      <StepHeading>
        What&rsquo;s <span className="text-primary">the duration</span> of your holiday?
      </StepHeading>

      <ul className="grid grid-cols-2 gap-2.5 md:gap-4 lg:grid-cols-4">
        {DURATION_BANDS.map((band) => {
          const isSelected = selected === band.id;
          return (
            <li key={band.id}>
              <button
                type="button"
                onClick={() => onSelect(band.id)}
                aria-pressed={isSelected}
                className={cn(
                    "relative h-full w-full rounded-xl border p-4 text-left transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary md:p-6 cursor-pointer",
                  isSelected
                    ? "-translate-y-1 border-primary bg-primary/[0.06] shadow-lg shadow-primary/20"
                    : "border-line bg-white shadow-sm hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10",
                )}
              >
                <h3
                  className={cn(
                    "pr-8 text-base font-bold md:text-lg md:font-extrabold",
                    isSelected ? "text-primary" : "text-headings",
                  )}
                >
                  {band.label}
                  <span className="mt-0.5 block text-sm font-normal text-muted-soft">{band.sub}</span>
                </h3>
                <p className="mt-3 hidden text-sm font-medium text-muted-soft md:block">{band.blurb}</p>
                {isSelected ? <CheckBadge /> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Step 3: Group Size ─────────────────────────────────────────────────────

function Incrementer({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <dl className="text-center">
      <dt className="mb-1.5 text-sm font-semibold text-headings">{label}</dt>
      <dd className="flex items-center justify-center gap-3">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="grid h-10 w-10 place-items-center rounded-full border border-line text-headings transition-colors hover:border-primary hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-30 cursor-pointer"
        >
          <Minus size={14} aria-hidden />
        </button>
        <span className="min-w-[1.5rem] text-center text-sm font-bold tabular-nums text-headings">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          className="grid h-10 w-10 place-items-center rounded-full border border-line text-headings transition-colors hover:border-primary hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-30 cursor-pointer"
        >
          <Plus size={14} aria-hidden />
        </button>
      </dd>
    </dl>
  );
}

function GroupSizeStep({
  groupSize,
  onSelectType,
  onAdultChange,
  onChildrenChange,
  onContinue,
}: {
  groupSize: GroupSize;
  onSelectType: (type: GroupType) => void;
  onAdultChange: (v: number) => void;
  onChildrenChange: (v: number) => void;
  onContinue: () => void;
}) {
  const showCounters = groupSize?.groupType === "family" || groupSize?.groupType === "friends";

  return (
    <div>
      <StepHeading>
        Who <span className="text-primary">is travelling</span> with you?
      </StepHeading>

      <ul className="flex flex-wrap items-center justify-center gap-3 md:gap-6">
        {GROUP_TYPES.map((type) => {
          const isSelected = groupSize?.groupType === type;
          return (
            <li key={type}>
              <button
                type="button"
                onClick={() => onSelectType(type)}
                aria-pressed={isSelected}
                className={cn(
                  "relative min-w-[140px] rounded-xl border px-6 py-7 text-center transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary cursor-pointer",
                  isSelected
                    ? "-translate-y-1 border-primary bg-primary/[0.06] shadow-lg shadow-primary/20"
                    : "border-line bg-white shadow-sm hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10",
                )}
              >
                <SpriteIcon
                  id={type}
                  size={56}
                  className={cn("mx-auto transition-colors", isSelected ? "text-primary" : "text-headings")}
                />
                <h3 className={cn("mt-3 text-lg font-extrabold capitalize", isSelected ? "text-primary" : "text-headings")}>
                  {type}
                </h3>
                {isSelected ? <CheckBadge /> : null}
              </button>
            </li>
          );
        })}
      </ul>

      {showCounters && groupSize ? (
        <div className="mx-auto mt-8 flex max-w-md justify-center gap-8 rounded-xl bg-bg px-6 py-4">
          <Incrementer label="Adults" value={groupSize.adult} min={1} max={25} onChange={onAdultChange} />
          <Incrementer label="Children" value={groupSize.children} min={0} max={25} onChange={onChildrenChange} />
        </div>
      ) : null}

      {groupSize ? (
        <div className="mt-8 text-center">
          <Button variant="outline" size="md" onClick={onContinue}>
            Continue <ChevronRight size={14} aria-hidden />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ─── Step 4: Budget ─────────────────────────────────────────────────────────

function BudgetStep({
  budget,
  onBudgetChange,
  flexible,
  onFlexibleChange,
}: {
  budget: [number, number];
  onBudgetChange: (v: [number, number]) => void;
  flexible: "yes" | "no" | null;
  onFlexibleChange: (v: "yes" | "no") => void;
}) {
  const [min, max] = budget;

  const handleMinChange = (v: number) => {
    onBudgetChange([Math.min(v, max - BUDGET_STEP), max]);
  };
  const handleMaxChange = (v: number) => {
    onBudgetChange([min, Math.max(v, min + BUDGET_STEP)]);
  };

  const minPercent = ((min - BUDGET_MIN) / (BUDGET_MAX - BUDGET_MIN)) * 100;
  const maxPercent = ((max - BUDGET_MIN) / (BUDGET_MAX - BUDGET_MIN)) * 100;

  return (
    <div>
      <StepHeading>
        What&rsquo;s your <span className="text-primary">budget</span> range?
      </StepHeading>

      <p className="mb-8 text-center text-sm text-muted-soft">
        Your estimated total budget
        <span className="mt-1 block text-2xl font-extrabold text-headings">
          {formatUsd(min)} – {formatUsd(max)}
        </span>
      </p>

      <div className="relative mx-auto h-8 max-w-xl">
        <div className="absolute left-0 right-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-line" />
        <div
          className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-primary"
          style={{ left: `${minPercent}%`, right: `${100 - maxPercent}%` }}
        />
        <input
          type="range"
          aria-label="Minimum budget"
          min={BUDGET_MIN}
          max={BUDGET_MAX}
          step={BUDGET_STEP}
          value={min}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          className="range-thumb pointer-events-none absolute inset-x-0 top-1/2 h-1.5 w-full -translate-y-1/2 appearance-none bg-transparent"
        />
        <input
          type="range"
          aria-label="Maximum budget"
          min={BUDGET_MIN}
          max={BUDGET_MAX}
          step={BUDGET_STEP}
          value={max}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          className="range-thumb pointer-events-none absolute inset-x-0 top-1/2 h-1.5 w-full -translate-y-1/2 appearance-none bg-transparent"
        />
      </div>

      <div className="mt-10 text-center">
        <h3 className="mb-4 text-sm font-semibold text-muted-soft">Would you be open to adjusting the budget?</h3>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <label
            className={cn(
              "relative flex cursor-pointer items-center gap-3 rounded-xl border py-4 pl-5 pr-12 text-left transition-all duration-300 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary",
              flexible === "yes"
                ? "border-primary bg-primary/[0.06] shadow-lg shadow-primary/20"
                : "border-line bg-white/70 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10",
            )}
          >
            <input
              type="radio"
              name="flexible-budget"
              value="yes"
              checked={flexible === "yes"}
              onChange={() => onFlexibleChange("yes")}
              className="sr-only"
            />
            <SpriteIcon
              id="flexiblebudget"
              size={40}
              className={cn("transition-colors", flexible === "yes" ? "text-primary" : "text-headings")}
            />
            <span className="text-sm font-medium leading-tight text-headings">
              Yes, I&rsquo;m flexible.
              <br /> Design me the best itinerary.
            </span>
            {flexible === "yes" ? (
              <i
                aria-hidden
                className="absolute right-4 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full border border-success bg-success text-white shadow-lg shadow-success/40"
              >
                <CheckCheck size={13} strokeWidth={2.5} />
              </i>
            ) : null}
          </label>
          <label
            className={cn(
              "relative flex cursor-pointer items-center gap-3 rounded-xl border py-4 pl-5 pr-12 text-left transition-all duration-300 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary",
              flexible === "no"
                ? "border-primary bg-primary/[0.06] shadow-lg shadow-primary/20"
                : "border-line bg-white/70 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10",
            )}
          >
            <input
              type="radio"
              name="flexible-budget"
              value="no"
              checked={flexible === "no"}
              onChange={() => onFlexibleChange("no")}
              className="sr-only"
            />
            <SpriteIcon
              id="fixedbudget"
              size={40}
              className={cn("transition-colors", flexible === "no" ? "text-primary" : "text-headings")}
            />
            <span className="text-sm font-medium leading-tight text-headings">
              No, I&rsquo;m not flexible
              <br /> that&rsquo;s my total budget.
            </span>
            {flexible === "no" ? (
              <i
                aria-hidden
                className="absolute right-4 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full border border-success bg-success text-white shadow-lg shadow-success/40"
              >
                <CheckCheck size={13} strokeWidth={2.5} />
              </i>
            ) : null}
          </label>
        </div>
      </div>
    </div>
  );
}

// ─── Step 5: Departure ──────────────────────────────────────────────────────

function DepartureStep({
  dateRange,
  onDateRangeChange,
  decideLater,
  onDecideLaterChange,
  onContinue,
}: {
  dateRange: DateRange | undefined;
  onDateRangeChange: (v: DateRange | undefined) => void;
  decideLater: boolean;
  onDecideLaterChange: (checked: boolean) => void;
  onContinue: () => void;
}) {
  const canContinue = decideLater || Boolean(dateRange?.from && dateRange?.to);

  return (
    <div>
      <StepHeading lede="Choose a date range.">
        When are you <span className="text-primary">scheduled</span> to go?
      </StepHeading>

      <div className={cn("overflow-x-auto rounded-xl border border-line bg-bg/50 p-2 sm:p-4", decideLater && "pointer-events-none opacity-50")}>
        <div className="flex min-w-max justify-center">
        <DayPicker
          mode="range"
          numberOfMonths={2}
          selected={dateRange}
          onSelect={onDateRangeChange}
          disabled={{ before: new Date() }}
          className="custom-date-picker"
        />
        </div>
      </div>

      <label className="mx-auto mt-6 flex w-fit cursor-pointer items-center justify-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 shadow-sm transition-colors hover:border-primary/50 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary">
        <input
          type="checkbox"
          checked={decideLater}
          onChange={(e) => onDecideLaterChange(e.target.checked)}
          className="h-4 w-4 rounded border-line text-primary focus-visible:ring-2 focus-visible:ring-primary/40"
        />
        <span className="text-sm font-bold text-headings underline underline-offset-2">I will decide later</span>
      </label>

      {canContinue ? (
        <div className="mt-8 text-center">
          <Button variant="outline" size="md" onClick={onContinue}>
            Continue <ChevronRight size={14} aria-hidden />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ─── Step 6: Results ────────────────────────────────────────────────────────

function TripCard({ pkg }: { pkg: PlannerPackage }) {
  return (
    <Link
      href={pkg.href}
      className="group flex h-full flex-col overflow-hidden rounded-xl border border-line bg-paper shadow-md shadow-headings/[0.06] transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <figure className="image-slot relative aspect-[306/200] w-full overflow-hidden">
        {pkg.image ? (
          <Image
            src={pkg.image}
            alt={pkg.imageAlt}
            fill
            sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            className="object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : null}
      </figure>
      <figcaption className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 text-sm font-bold text-headings">{pkg.title}</h3>
        <div className="mt-auto flex items-end justify-between pt-3">
          {pkg.duration ? <span className="text-xs text-muted-soft">{pkg.duration}</span> : <span />}
          {pkg.price ? <span className="text-sm font-bold text-primary">{pkg.price}</span> : null}
        </div>
      </figcaption>
    </Link>
  );
}

function ResultsStep({
  matches,
  destinationName,
  onPickTrip,
}: {
  matches: PlannerPackage[];
  destinationName: string | null;
  onPickTrip: () => void;
}) {
  return (
    <div>
      <StepHeading
        lede={
          matches.length > 0
            ? `Handpicked ${destinationName ? `for ${destinationName}` : "for your criteria"} — browse below, or tell us more and we'll tailor one for you.`
            : "None of our set itineraries fit exactly — share your plan with us and we'll build a custom one."
        }
      >
        {matches.length > 0 ? (
          <>
            Start with these <span className="text-primary">popular choices</span>
          </>
        ) : (
          "No exact matches yet"
        )}
      </StepHeading>

      {matches.length > 0 ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {matches.slice(0, 6).map((pkg) => (
            <TripCard key={pkg.id} pkg={pkg} />
          ))}
        </div>
      ) : null}

      <div className="mt-8 flex justify-center">
        <Button variant="outline" size="sm" onClick={onPickTrip}>
          {matches.length > 0 ? "None of these fit — build a custom trip" : "Get a custom itinerary"}
        </Button>
      </div>
    </div>
  );
}

// ─── Step 7: Contact ────────────────────────────────────────────────────────

function ContactStep({
  destinationName,
  durationLabel,
  groupLabel,
  budgetLabel,
  dateLabel,
  matchCount,
}: {
  destinationName: string | null;
  durationLabel?: string;
  groupLabel?: string;
  budgetLabel?: string;
  dateLabel?: string;
  matchCount: number;
}) {
  const [ts] = useState(() => String(Date.now()));
  const formRef = useRef<HTMLFormElement>(null);
  const [state, action, pending] = useActionState<InquiryState | null, FormData>(submitInquiry, null);

  const summary = [
    destinationName ? `Destination: ${destinationName}` : null,
    durationLabel ? `Duration: ${durationLabel}` : null,
    groupLabel ? `Group: ${groupLabel}` : null,
    budgetLabel ? `Budget: ${budgetLabel}` : null,
    dateLabel ? `Travel dates: ${dateLabel}` : null,
    `Matching set itineraries: ${matchCount}`,
  ]
    .filter(Boolean)
    .join("\n");

  useEffect(() => {
    if (state?.ok) formRef.current?.reset();
  }, [state?.ok]);

  const err = state && !state.ok && state.code === "validation" ? (state.fields ?? {}) : {};

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!e.currentTarget.reportValidity()) return;
    const formData = new FormData(e.currentTarget);
    const notes = String(formData.get("notes") ?? "").trim();
    formData.set(
      "message",
      notes ? `Built with the trip planner.\n${summary}\n\nNotes: ${notes}` : `Built with the trip planner.\n${summary}`,
    );
    startTransition(() => action(formData));
  };

  return (
    <div>
      <StepHeading lede="We'll use your plan to put together a tailored itinerary and quote.">
        Almost there — <span className="text-primary">how can we reach you?</span>
      </StepHeading>

      {state?.ok ? (
        <div role="status" className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm font-semibold text-emerald-800">{state.message}</p>
        </div>
      ) : (
        <form ref={formRef} action={action} onSubmit={handleSubmit} className="space-y-4">
          <input type="hidden" name="ts" value={ts} />
          <input type="hidden" name="type" value="Customize trip" />
          <input type="hidden" name="subject" value="Trip planner enquiry" />
          <input type="hidden" name="message" value="" />
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden
            className="absolute left-[-9999px]"
          />

          <div className="rounded-xl border border-line bg-bg p-4 text-xs leading-5 text-muted-soft">
            <p className="font-semibold text-headings">Your plan at a glance</p>
            <p className="mt-1.5 whitespace-pre-line">{summary}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="planner-name">Your name</Label>
              <Input id="planner-name" name="name" required autoComplete="name" error={Boolean(err.name)} />
              {err.name ? <p className="mt-1 text-xs text-red-600">{err.name}</p> : null}
            </div>
            <div>
              <Label htmlFor="planner-email">Email</Label>
              <Input
                id="planner-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                error={Boolean(err.email)}
              />
              {err.email ? <p className="mt-1 text-xs text-red-600">{err.email}</p> : null}
            </div>
            <div>
              <Label htmlFor="planner-phone">Phone (optional)</Label>
              <Input id="planner-phone" name="phone" type="tel" autoComplete="tel" />
            </div>
            <div>
              <Label htmlFor="planner-travelers">Travelers (optional)</Label>
              <Input id="planner-travelers" name="travelers" type="number" min={1} />
            </div>
          </div>

          <div>
            <Label htmlFor="planner-message">Anything else we should know?</Label>
            <Textarea
              id="planner-message"
              name="notes"
              rows={3}
              placeholder="Preferred travel dates, must-see places, accessibility needs…"
            />
          </div>

          {state && !state.ok ? (
            <p className="text-sm text-red-600" role="alert">
              {state.message}
            </p>
          ) : null}

          <Button type="submit" variant="primary" size="md" disabled={pending} className="w-full sm:w-auto">
            {pending ? (
              <>
                <Loader2 size={16} className="animate-spin" aria-hidden /> Sending…
              </>
            ) : (
              "Send my trip plan"
            )}
          </Button>
        </form>
      )}
    </div>
  );
}
